import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { IoIosArrowDown } from "react-icons/io";
import { Play, Pause } from "lucide-react";
import { updateBasicDetails } from "../../Services/MyProfile";
import { useHeader } from "../../Providers/HeaderContextProvider";
import { getAudioFile, getVoiceOptions, getVoiceOptionByValueOrId } from "../../Utils/CommonFunctions/voiceMapping";
import "./VoicePreference.css";

// Lightweight, reusable Voice Preference editor for Settings
// Refactored to be controlled by GenericModal
const VoicePreference = forwardRef(function VoicePreference({ onClose, onLoadingChange }, ref) {
  const { profileContent, addProfileData } = useHeader();

  // Get the voice value - handle both value (e.g., "Female") and openAI voice ID (e.g., "echo")
  const initialVoice = useMemo(() => {
    const voiceFromProfile = profileContent?.aris_voice || "";
    const voiceOption = getVoiceOptionByValueOrId(voiceFromProfile);
    // Return the value (e.g., "Female") for the dropdown, not the openAI ID
    return voiceOption?.value || voiceFromProfile;
  }, [profileContent?.aris_voice]);
  
  const [selectedVoice, setSelectedVoice] = useState(initialVoice);
  
  // Update selectedVoice when initialVoice changes (e.g., when profile loads)
  useEffect(() => {
    if (initialVoice) {
      setSelectedVoice(initialVoice);
    }
  }, [initialVoice]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const audioRef = useRef(null);

  const options = useMemo(() => getVoiceOptions(), []);

  useEffect(() => {
    return () => {
      try { audioRef.current?.pause(); } catch (_) {}
    };
  }, []);

  const handlePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }

    audioRef.current.currentTime = 0;
    audioRef.current.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
  };

  const formatDateForDisplay = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
  };

  const handleSave = async () => {
    setError("");
    setSuccess("");

    if (!selectedVoice) {
      setError("Please select a voice.");
      return;
    }

    setLoading(true);
    if (typeof onLoadingChange === "function") onLoadingChange(true);
    try {
      // Preserve existing profile fields while updating only the voice
      const details = {
        first_name: profileContent?.first_name || "",
        last_name: profileContent?.last_name || "",
        date_of_birth: profileContent?.DOB ? formatDateForDisplay(profileContent.DOB) : "",
        gender: profileContent?.gender || "",
        aris_voice: selectedVoice,
      };
      await updateBasicDetails(details);

      const updated = { ...profileContent, aris_voice: selectedVoice };
      try { localStorage.setItem("profile", JSON.stringify(updated)); } catch (_) {}
      addProfileData(updated);
      setSuccess("Voice preference updated.");
      setTimeout(() => { onClose?.(); }, 400);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to update voice");
    } finally {
      setLoading(false);
      if (typeof onLoadingChange === "function") onLoadingChange(false);
    }
  };

  useImperativeHandle(ref, () => ({
    save: handleSave,
  }));

  return (
    <div className="voice-dialog">

      <div className="voice-dialog__controls">
        <div className="voice-dialog__select-wrapper">
          <select
            value={selectedVoice}
            onChange={(e) => setSelectedVoice(e.target.value)}
            className="voice-dialog__select"
          >
            <option value="" disabled>Select your voice preference</option>
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <IoIosArrowDown className="voice-dialog__select-icon" />
        </div>

        <button
          type="button"
          className="voice-dialog__preview"
          onClick={handlePlayPause}
          disabled={!selectedVoice}
        >
          {isPlaying ? <Pause size={20} /> : <Play size={20} />}
        </button>
      </div>

      <audio ref={audioRef} src={selectedVoice ? getAudioFile(selectedVoice) : ""} onEnded={() => setIsPlaying(false)} />

      {error && (
        <div className="voice-dialog__alert voice-dialog__alert--error" role="alert">
          {error}
        </div>
      )}
      {success && (
        <div className="voice-dialog__alert voice-dialog__alert--success" role="status">
          {success}
        </div>
      )}

      {/* Actions moved to GenericModal */}
    </div>
  );
});

export default VoicePreference;
